/* eslint-disable eqeqeq */
import type {
  ISizerCasingInsulation,
  ISizerCoilMfgSettings,
  ISizerConstructionClass,
  ISizerFilterSize,
  ISizerMasterRow,
  ISizerMatrixRequest,
} from 'app/services/sizerApi';

/**
 * Phase 4b — the pure model behind `tmpl/settings.tmpl` (the VISIBLE block,
 * lines 1-329 + 681-685) and behind `updateMatrixData` (view/main.js:478-570).
 *
 * ★ THE HIDDEN DUPLICATE IS NOT PORTED. `settings.tmpl:330-679` renders a
 * SECOND, `.hide`-wrapped copy of every Cooling/Heating/Filter control — 34
 * `.cached` elements where only ~17 are visible (OBSERVED duplicate `name`
 * counts in `p4/notes2.json`: `cc_face_vel_min`×2 … `filter_face_vel_min`×3).
 * It is invisible but not inert: it carries `.cached`, so `changeCached` binds
 * to it and the restore loop writes to it. Legacy reads are safe only because
 * `self.$('input[name=…]')` takes the FIRST match, which is always the visible
 * one. Modelling ONE value per control name reproduces every observable
 * behaviour of that arrangement exactly (discovery §6c + §10). The hidden
 * `filter_frame_loading` select (settings.tmpl:655-668) is dropped with it —
 * its collection is never fetched, so it renders empty, and the payload
 * hardcodes `filter_frame_loading_id: 1` regardless.
 */

// ---------------------------------------------------------------------------
// field inventory
// ---------------------------------------------------------------------------

/**
 * Every `.cached` control of the VISIBLE settings block, in DOM order — the
 * order the localStorage restore loop walks (main.js:301) and therefore the
 * order in which the `_min`/`_max` sibling attribute writes land.
 */
export const SIZER_CACHED_FIELDS = [
  'construction_class_id',
  'casing_insulation',
  'cc_face_vel_min',
  'cc_face_vel_max',
  'cc_tube',
  'cc_max_fh',
  'cc_top_and_btm_casing_flange',
  'cc_qty_stacked',
  'hc_face_vel_min',
  'hc_face_vel_max',
  'hc_tube',
  'hc_max_fh',
  'hc_top_and_btm_casing_flange',
  'hc_qty_stacked',
  'max_different_sizes',
  'filter_face_vel_min',
  'filter_face_vel_max',
] as const;

export type SizerFieldName = (typeof SIZER_CACHED_FIELDS)[number];

export type SizerFieldValues = Record<SizerFieldName, string>;

/**
 * `changeCached`'s `_min`/`_max` cross-link (main.js:445-459) walks the DOM with
 * `.next()` / `.prev()`, so it only works because each pair sits as two ADJACENT
 * inputs inside one `.col-xs-5.row.d-flex` (settings.tmpl:49-68, :141-160,
 * :290-305). Modelled explicitly here, as the discovery requires.
 *
 * ⚠ `max_different_sizes` contains neither `_min` nor `_max`, and
 * `cc_qty_stacked` / `hc_qty_stacked` contain neither — they are NOT pairs and
 * legacy never cross-links them.
 */
export const SIZER_MIN_MAX_PAIRS: ReadonlyArray<{ min: SizerFieldName; max: SizerFieldName }> = [
  { min: 'cc_face_vel_min', max: 'cc_face_vel_max' },
  { min: 'hc_face_vel_min', max: 'hc_face_vel_max' },
  { min: 'filter_face_vel_min', max: 'filter_face_vel_max' },
];

/** name → its sibling in the pair (undefined for unpaired fields). */
export function pairSibling(name: SizerFieldName): SizerFieldName | undefined {
  for (const p of SIZER_MIN_MAX_PAIRS) {
    if (p.min === name) return p.max;
    if (p.max === name) return p.min;
  }
  return undefined;
}

/** Legacy's own test (`name.includes('_min')` / `'_max'`, main.js:445, :452). */
export function isMinField(name: string): boolean {
  return name.includes('_min');
}
export function isMaxField(name: string): boolean {
  return name.includes('_max');
}

// ---------------------------------------------------------------------------
// data-min / data-max — the ATTRIBUTES, and jQuery's cache of them
// ---------------------------------------------------------------------------

/** One control's live `data-min`/`data-max` ATTRIBUTE values (`null` = absent). */
export interface SizerAttrBounds {
  min: string | null;
  max: string | null;
}

export type SizerBoundsMap = Record<SizerFieldName, SizerAttrBounds>;

/**
 * ★ jQuery `.data()` CACHING — reproduced deliberately.
 *
 * `changeCached` reads its clamp window with `input.data('min')` /
 * `input.data('max')` (main.js:440-441), and `validateNumberField` does the
 * same (:340-341). jQuery `.data(key)` populates its internal store from the
 * `data-*` ATTRIBUTE on FIRST access and then never re-reads the attribute —
 * so the cross-link's `$(el).next().attr('data-min', v)` (:446) updates the
 * attribute but NOT an already-cached `.data('min')`.
 *
 * Net observable rule: **a control's effective clamp window freezes at the
 * attribute values it had when it FIRST fired a change, and only unfreezes on
 * a full re-render** (which builds brand-new elements with an empty store).
 * The one exception is `applySizes`, which writes the store DIRECTLY with
 * `.data('max', n)` (:798) — modelled by `setCachedBound` below.
 *
 * `attrs` is the live attribute state; `cache` is jQuery's store.
 */
export interface SizerBoundsState {
  attrs: SizerBoundsMap;
  cache: Partial<Record<SizerFieldName, { min?: number | string; max?: number | string }>>;
}

/**
 * jQuery's `data-*` → JS coercion (`jQuery.data`'s `getData`): "150" → 150,
 * "0" → 0, "" → "", non-numeric → the raw string.
 */
function jqDataCoerce(raw: string | null): number | string | undefined {
  if (raw == null) return undefined;
  if (raw === '') return '';
  const n = Number(raw);
  return Number.isNaN(n) ? raw : n;
}

/** `input.data(k) ? Number(input.data(k)) : fallback` (main.js:440-441). ⚠ a
 * `data-min="0"` is FALSY, so `cc_qty_stacked` effectively has NO lower clamp
 * even though the template declares `data-min="0"` (settings.tmpl:126). */
function boundOrFallback(value: number | string | undefined, fallback: number): number {
  return value ? Number(value) : fallback;
}

/** Read a bound the way `changeCached` does, populating jQuery's cache. */
export function readBound(
  state: SizerBoundsState,
  name: SizerFieldName,
  which: 'min' | 'max'
): number {
  const entry = state.cache[name] ?? {};
  if (!(which in entry)) {
    entry[which] = jqDataCoerce(state.attrs[name]?.[which] ?? null);
    state.cache[name] = entry;
  }
  return boundOrFallback(entry[which], which === 'min' ? -Infinity : Infinity);
}

/** `$(el).attr('data-min'|'data-max', v)` — attribute only, cache untouched. */
export function setBoundAttr(
  state: SizerBoundsState,
  name: SizerFieldName,
  which: 'min' | 'max',
  value: string
): void {
  const attrs = state.attrs[name] ?? { min: null, max: null };
  state.attrs[name] = { ...attrs, [which]: value };
}

/** `$(el).data('max', v)` — jQuery's store DIRECTLY (applySizes, main.js:798). */
export function setCachedBound(
  state: SizerBoundsState,
  name: SizerFieldName,
  which: 'min' | 'max',
  value: number
): void {
  state.cache[name] = { ...(state.cache[name] ?? {}), [which]: value };
}

// ---------------------------------------------------------------------------
// <option> model
// ---------------------------------------------------------------------------

export interface SizerOption {
  /** the `value` attribute — what `select.value` and the cache store. */
  value: string;
  /** the rendered text — what the matrix payload reads for `*c_tube`. */
  text: string;
  /** `data-id` — cached as `{name}_id` (main.js:462). */
  dataId?: string;
  /** `data-value` — cached as `{name}_value` (main.js:463). */
  dataValue?: string;
  /** extra class (`coil_max_fh.tmpl:1` puts `cc_max_fh`/`hc_max_fh` here). */
  className?: string;
  /** the master-row envelope id — only Max-FH selects need it (see below). */
  rowId?: number;
}

/**
 * Browser semantics for a template that may stamp `selected` on more than one
 * option: the LAST one wins; with none, the first option is selected. Both
 * happen here — the Cooling Casing Flange select renders a `selected` blank
 * option AND a `selected` 1.5 option (settings.tmpl:104-111).
 */
function initialSelected(options: SizerOption[], isSelected: (o: SizerOption, i: number) => boolean): string {
  let value: string | null = null;
  options.forEach((o, i) => {
    if (isSelected(o, i)) value = o.value;
  });
  if (value != null) return value;
  return options.length ? options[0].value : '';
}

/** Handlebars renders a JS number with `String()` — 1.0 → "1", 0.625 → "0.625". */
function hbs(value: unknown): string {
  return value == null ? '' : String(value);
}

/** Product Class — settings.tmpl:12-18. Label `{{get_one label construction_class}}`
 * → `label || construction_class || ''` (helpers.js:94-96). */
export function buildConstructionClassOptions(rows: ISizerConstructionClass[]): SizerOption[] {
  return rows.map((r) => ({
    value: hbs(r.id),
    text: r.label || r.construction_class || '',
  }));
}

/** Casing Insulation — settings.tmpl:36-40, label `{{model}}`. */
export function buildCasingInsulationOptions(rows: ISizerCasingInsulation[]): SizerOption[] {
  return rows.map((r) => ({ value: hbs(r.id), text: hbs(r.model) }));
}

/** `default_casing` (main.js:251): the row whose thickness is EXACTLY 2, else
 * the first row. `?.id` → `undefined` for an empty collection, which renders
 * `{{#ifCompare undefined '==' id}}` false everywhere → the first option. */
export function defaultCasingInsulationId(rows: ISizerCasingInsulation[]): number | undefined {
  return (rows.find((r) => r.thickness === 2) || rows[0])?.id;
}

/** Tube Diameter — settings.tmpl:77-83. `value` is `item.id` (NOT the envelope
 * id) which is why the Max-FH URL carries `tube_dias/4/`; the payload reads the
 * option TEXT (`item.value`). */
export function buildTubeOptions(rows: ISizerMasterRow[]): SizerOption[] {
  return rows.map((r) => ({
    value: hbs(r.item.id),
    text: hbs(r.item.value),
    dataValue: hbs(r.item.value),
    rowId: r.id,
  }));
}

/**
 * Casing Flange — cooling settings.tmpl:103-112 (leading blank `- -` option),
 * heating :195-201 (NO blank). Both stamp `selected` on `item.id == 5`.
 */
export function buildFlangeOptions(rows: ISizerMasterRow[], withBlank: boolean): SizerOption[] {
  const opts: SizerOption[] = rows.map((r) => ({
    value: hbs(r.item.value),
    text: hbs(r.item.value),
    dataId: hbs(r.item.id),
    rowId: r.id,
  }));
  return withBlank ? [{ value: '', text: '- -' }, ...opts] : opts;
}

/**
 * Fin Height Maximum — the select is EMPTY in the template (settings.tmpl:93,
 * :185) and filled imperatively by `updateMaxFH` (main.js:176-211) from
 * `coil_max_fh.tmpl`: `value="{{max_fh.item.name}}"`, `class="{{prefix}}_max_fh"`.
 */
export function buildMaxFhOptions(rows: ISizerMasterRow[], prefix: 'cc' | 'hc'): SizerOption[] {
  return rows.map((r) => ({
    value: hbs(r.item.name),
    text: hbs(r.item.name),
    className: `${prefix}_max_fh`,
    rowId: r.id,
  }));
}

/**
 * `updateMaxFH`'s selection, in legacy order (main.js:192-210):
 *  1. `id` = the ENVELOPE id of the row whose `item.value == 48` (cooling) or
 *     `== 60` (heating); `coil_max_fh.tmpl:2-3` stamps `selected` on that row.
 *     ⚠ When no row matches, `id` is `undefined`, nothing is selected and the
 *     browser falls back to the FIRST option. That is reachable: tube item 3
 *     (1/2") has no 48.0 fin height on dev3, so switching Cooling to 1/2"
 *     lands on 6.25 in — legacy behaviour, reproduced.
 *  2. THEN the cached `sizer_settings[cc_max_fh|hc_max_fh]` overrides it, but
 *     only if an option with that value still exists (:207-209). The cache is
 *     NOT invalidated by a tube change, so a stale value from another tube's
 *     list wins whenever it happens to exist in the new list too.
 */
export function resolveMaxFhValue(
  options: SizerOption[],
  rows: ISizerMasterRow[],
  isCooling: boolean,
  cached: unknown
): string {
  const target = isCooling ? 48 : 60;
  const rowId = rows.find((r) => r.item.value == target)?.id;
  let value = initialSelected(options, (o) => rowId != null && o.rowId === rowId);
  if (cached !== undefined && cached !== null) {
    const cachedStr = String(cached);
    if (options.some((o) => o.value == cachedStr)) value = cachedStr;
  }
  return value;
}

// ---------------------------------------------------------------------------
// initial state
// ---------------------------------------------------------------------------

export interface SizerCollections {
  constructionClass: ISizerConstructionClass[];
  casingInsulations: ISizerCasingInsulation[];
  tubeDias: ISizerMasterRow[];
  casingFlangeWidths: ISizerMasterRow[];
  coolingCoilSettings: ISizerCoilMfgSettings | null;
  heatingCoilSettings: ISizerCoilMfgSettings | null;
}

export interface SizerOptionSets {
  constructionClass: SizerOption[];
  casingInsulation: SizerOption[];
  ccTube: SizerOption[];
  hcTube: SizerOption[];
  ccFlange: SizerOption[];
  hcFlange: SizerOption[];
}

export function buildOptionSets(c: SizerCollections): SizerOptionSets {
  const tube = buildTubeOptions(c.tubeDias);
  return {
    constructionClass: buildConstructionClassOptions(c.constructionClass),
    casingInsulation: buildCasingInsulationOptions(c.casingInsulations),
    ccTube: tube,
    hcTube: tube,
    ccFlange: buildFlangeOptions(c.casingFlangeWidths, true),
    hcFlange: buildFlangeOptions(c.casingFlangeWidths, false),
  };
}

/**
 * The values `settings.tmpl` renders BEFORE the localStorage restore pass —
 * i.e. `render()`'s output (main.js:273-274). The Max-FH pair is deliberately
 * absent (`''`): those selects are empty until `updateMaxFH` runs, which is
 * also why their entry in the restore loop is a no-op (`cachedInput.options`
 * is an empty collection, so the `find` at :315 fails).
 */
export function buildTemplateValues(c: SizerCollections, options: SizerOptionSets): SizerFieldValues {
  const cooling = c.coolingCoilSettings;
  const heating = c.heatingCoilSettings;
  const defaultCasing = defaultCasingInsulationId(c.casingInsulations);
  return {
    // settings.tmpl:15 — `{{#ifCompare this.id '==' 1}}selected{{/ifCompare}}`
    construction_class_id: initialSelected(options.constructionClass, (o) => o.value == '1'),
    // :38 — `{{#ifCompare ../default_casing '==' id}}`
    casing_insulation: initialSelected(
      options.casingInsulation,
      (o) => defaultCasing != null && o.value == String(defaultCasing)
    ),
    cc_face_vel_min: '200', // :57
    cc_face_vel_max: '500', // :66
    // :80 — `{{#ifCompare @index '==' 2}}` (5/8" on dev3)
    cc_tube: initialSelected(options.ccTube, (_o, i) => i === 2),
    cc_max_fh: '',
    // :109 — `{{#ifCompare this.item.id '==' 5}}` AFTER a selected blank :104
    cc_top_and_btm_casing_flange: initialSelected(options.ccFlange, (o) => o.dataId == '5'),
    cc_qty_stacked: hbs(cooling?.max_qty_stacked), // :131
    hc_face_vel_min: '200', // :149
    hc_face_vel_max: '800', // :158
    hc_tube: initialSelected(options.hcTube, (_o, i) => i === 2), // :172
    hc_max_fh: '',
    hc_top_and_btm_casing_flange: initialSelected(options.hcFlange, (o) => o.dataId == '5'), // :198
    hc_qty_stacked: hbs(heating?.max_qty_stacked), // :219
    max_different_sizes: '3', // :280
    filter_face_vel_min: '200', // :296
    filter_face_vel_max: '600', // :303
  };
}

/** The `data-min`/`data-max` ATTRIBUTES the template stamps. `null` = the
 * attribute is absent (the two Filter face-velocity inputs, :291-304). */
export function buildTemplateBounds(c: SizerCollections): SizerBoundsMap {
  const cooling = c.coolingCoilSettings;
  const heating = c.heatingCoilSettings;
  const ccMin = cooling ? hbs(cooling.min_face_velocity) : null;
  const ccMax = cooling ? hbs(cooling.max_face_velocity) : null;
  const hcMin = heating ? hbs(heating.min_face_velocity) : null;
  const hcMax = heating ? hbs(heating.max_face_velocity) : null;
  return {
    construction_class_id: { min: null, max: null },
    casing_insulation: { min: null, max: null },
    cc_face_vel_min: { min: ccMin, max: ccMax }, // :55-56
    cc_face_vel_max: { min: ccMin, max: ccMax }, // :64-65
    cc_tube: { min: null, max: null },
    cc_max_fh: { min: null, max: null },
    cc_top_and_btm_casing_flange: { min: null, max: null },
    // :126-127 — data-min="0" is FALSY to `input.data('min') ? … : -Infinity`
    cc_qty_stacked: {
      min: cooling ? hbs(cooling.min_qty_stacked) : null,
      max: cooling ? hbs(cooling.max_qty_stacked) : null,
    },
    hc_face_vel_min: { min: hcMin, max: hcMax }, // :147-148
    hc_face_vel_max: { min: hcMin, max: hcMax }, // :156-157
    hc_tube: { min: null, max: null },
    hc_max_fh: { min: null, max: null },
    hc_top_and_btm_casing_flange: { min: null, max: null },
    hc_qty_stacked: {
      min: heating ? hbs(heating.min_qty_stacked) : null,
      max: heating ? hbs(heating.max_qty_stacked) : null,
    }, // :214-216
    max_different_sizes: { min: '1', max: '6' }, // :276-277
    filter_face_vel_min: { min: null, max: null },
    filter_face_vel_max: { min: null, max: null },
  };
}

/** The default multiselect commit (main.js:249) — filter-size ids 1,2,7,8,9,10. */
export const DEFAULT_ALLOWABLE_FILTER_SIZE_IDS = [1, 2, 7, 8, 9, 10];

export function defaultAllowableFilterSizes(rows: ISizerFilterSize[]): ISizerFilterSize[] {
  return rows.filter((el) => DEFAULT_ALLOWABLE_FILTER_SIZE_IDS.includes(el.id));
}

/**
 * The restore pass (main.js:301-325), applied to the freshly-templated state.
 *
 *  - `_min`/`_max` names first write the SIBLING's `data-min`/`data-max`
 *    ATTRIBUTE from the cached value (:305-309) — before any value is applied.
 *  - a SELECT only accepts the cached value when an option with it still
 *    exists (:313-317); an INPUT takes it unconditionally (:318-320).
 *  - `cachedInput.setValue` (:311) is dead in the sizer: no template stamps
 *    `data-formatter_type`, so `fn.uni_tools.bindFormatter` never installs the
 *    Formatter binder that would define it (verified: zero `formatter_type`
 *    attributes in `sizer/**`). Same reason `getValue` at :461 never exists.
 */
export function applyCachedValues(
  values: SizerFieldValues,
  bounds: SizerBoundsMap,
  options: SizerOptionSets,
  cache: Record<string, unknown>
): { values: SizerFieldValues; bounds: SizerBoundsMap } {
  const nextValues = { ...values };
  const nextBounds: SizerBoundsMap = { ...bounds };
  const selectOptions: Partial<Record<SizerFieldName, SizerOption[]>> = {
    construction_class_id: options.constructionClass,
    casing_insulation: options.casingInsulation,
    cc_tube: options.ccTube,
    hc_tube: options.hcTube,
    cc_top_and_btm_casing_flange: options.ccFlange,
    hc_top_and_btm_casing_flange: options.hcFlange,
    // cc_max_fh / hc_max_fh are EMPTY at restore time — see resolveMaxFhValue.
    cc_max_fh: [],
    hc_max_fh: [],
  };

  for (const name of SIZER_CACHED_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(cache, name)) continue;
    const cached = cache[name];
    const cachedStr = cached == null ? '' : String(cached);

    const sibling = pairSibling(name);
    if (isMinField(name) && sibling) {
      nextBounds[sibling] = { ...nextBounds[sibling], min: cachedStr };
    } else if (isMaxField(name) && sibling) {
      nextBounds[sibling] = { ...nextBounds[sibling], max: cachedStr };
    }

    const opts = selectOptions[name];
    if (opts) {
      if (opts.some((o) => o.value == cachedStr)) nextValues[name] = cachedStr;
    } else {
      nextValues[name] = cachedStr;
    }
  }
  return { values: nextValues, bounds: nextBounds };
}

// ---------------------------------------------------------------------------
// the matrix payload
// ---------------------------------------------------------------------------

/** `.val().replace(',', '')` — ⚠ String.replace with a STRING pattern strips
 * only the FIRST comma, so "1,234,567" becomes "1234,567" → `+x` is NaN. Kept
 * verbatim (main.js:483, :487, …). */
function stripFirstComma(value: string): string {
  return value.replace(',', '');
}

export interface BuildMatrixPayloadArgs {
  /** the raw `input[name=airflow]` value, commas and all. */
  airflow: string;
  values: SizerFieldValues;
  options: SizerOptionSets;
  casingInsulations: ISizerCasingInsulation[];
  /** the COMMITTED multiselect (`self.allowable_filter_sizes`). */
  allowableFilterSizes: ISizerFilterSize[];
}

/** The `+el.options[el.selectedIndex].text` read of main.js:532-535 / :542-545. */
function selectedOptionText(options: SizerOption[], value: string): number {
  const opt = options.find((o) => o.value == value);
  return opt ? Number(opt.text) : NaN;
}

/**
 * `+self.$('select[name=…] option[value="${x.toFixed(2)}"]')?.attr('data-id')`
 * (main.js:537, :547). The option's value attribute is `1.5`, the selector asks
 * for `1.50` → no match → `.attr()` on an empty jQuery set is `undefined` →
 * `+undefined` is `NaN` → `JSON.stringify` emits `null`.
 *
 * The MECHANISM is reproduced rather than the constant, so the port would
 * follow legacy if the master data ever grew a `1.50`-valued option.
 */
function flangeIdLookup(options: SizerOption[], flange: number): number {
  const wanted = flange.toFixed(2);
  const opt = options.find((o) => o.value === wanted);
  return Number(opt?.dataId);
}

/**
 * `updateMatrixData`'s POST body (main.js:520-562), key-for-key in source
 * order. See `ISizerMatrixRequest` for the quirk inventory.
 *
 * ⚠ Every read is of the FIRST element with that name — i.e. the visible
 * control — which is what modelling one value per name gives us for free.
 */
export function buildMatrixPayload(args: BuildMatrixPayloadArgs): ISizerMatrixRequest {
  const { airflow, values, options, casingInsulations } = args;

  const insulationValue = values.casing_insulation;
  const insulation = insulationValue
    ? casingInsulations.find((r) => String(r.id) === insulationValue)
    : undefined;

  const ccFlange = Number(values.cc_top_and_btm_casing_flange);
  const hcFlange = Number(values.hc_top_and_btm_casing_flange);

  let allowable = '';
  args.allowableFilterSizes.forEach((el, idx) => {
    allowable += `${el.value}${args.allowableFilterSizes.length - 1 !== idx ? ',' : ''}`;
  });

  return {
    airflow: Number(stripFirstComma(airflow)),
    product_name: 1,
    // main.js:523 — `collection.get(id).get('thickness')`; no selection → null.
    // (Legacy would THROW on a selection whose row is missing; unreachable
    // because the value always comes from the rendered option list.)
    thickness: insulationValue ? insulation?.thickness ?? null : null,
    // :524-529 — no template renders these inputs, so `+undefined || 8`.
    plenum_fan_max_wide: 8,
    plenum_fan_max_high: 8,
    cube_fan_max_wide: 8,
    cube_fan_max_high: 8,
    plug_fan_max_wide: 8,
    plug_fan_max_high: 8,
    cc_face_vel_min: Number(stripFirstComma(values.cc_face_vel_min)),
    cc_face_vel_max: Number(stripFirstComma(values.cc_face_vel_max)),
    cc_tube: selectedOptionText(options.ccTube, values.cc_tube),
    cc_top_and_btm_casing_flange: ccFlange,
    cc_top_and_btm_casing_flange_id: flangeIdLookup(options.ccFlange, ccFlange),
    cc_max_fh: Number(values.cc_max_fh),
    cc_qty_stacked: Number(values.cc_qty_stacked),
    hc_face_vel_min: Number(stripFirstComma(values.hc_face_vel_min)),
    hc_face_vel_max: Number(stripFirstComma(values.hc_face_vel_max)),
    hc_tube: selectedOptionText(options.hcTube, values.hc_tube),
    hc_top_and_btm_casing_flange: hcFlange,
    hc_top_and_btm_casing_flange_id: flangeIdLookup(options.hcFlange, hcFlange),
    hc_max_fh: Number(values.hc_max_fh),
    hc_qty_stacked: Number(values.hc_qty_stacked),
    filter_face_vel_min: Number(stripFirstComma(values.filter_face_vel_min)),
    filter_face_vel_max: Number(stripFirstComma(values.filter_face_vel_max)),
    max_different_sizes: Number(stripFirstComma(values.max_different_sizes)),
    filter_frame_loading_id: 1,
    construction_class_id: Number(values.construction_class_id) || 1,
    casing_insulation_id: Number(values.casing_insulation),
    allowable_filter_sizes: allowable,
  };
}
